import { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Stack, Alert, CircularProgress, Card, CardContent,
    IconButton, Tooltip, Chip, Pagination, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';


/**
 * Vietu moderācija - DIVAS atsevišķas rindas, jo tie ir divi dažādi lēmumi:
 *
 * 1. "Jaunas vietas" - SP pievienoja + samaksāja; lēmums padara vietu publisku
 *    (Active=true) vai atstāj slēptu. Neapmaksātas vietas rindā neparādās.
 *
 * 2. "Izmaiņas" - SP rediģēja JAU APSTIPRINĀTU vietu. Vieta pa to laiku joprojām ir
 *    publiski redzama ar VECO saturu (overlay - place_pending_changes), tāpēc lēmums
 *    nosaka tikai to, vai jauno saturu publicēt. Rāda vecais→jaunais diff.
 *
 * Server endpoint'i (AdminModerationController.cs):
 *   POST /api/v1/admin/moderation/places/pending          - jaunas vietas
 *   POST /api/v1/admin/moderation/places/decide           - lēmums par jaunu vietu
 *   POST /api/v1/admin/moderation/places/changes/pending  - izmaiņas ar old/new
 *   POST /api/v1/admin/moderation/places/changes/decide   - lēmums par izmaiņu
 *   GET  /api/v1/admin/moderation/places/reject-reasons   - iemesli dropdown
 */
type PendingPlaceItem = {
    placeId: string;
    placeAddress: string | null;
    placeKindName: string | null;
    description: string | null;
    ownerUserId: string | null;
    createdAt: string;
    isPaid: boolean;
};

type PendingResponse = {
    isSuccess: boolean;
    error: string | null;
    totalPending: number;
    items: PendingPlaceItem[];
};

type PendingChangeItem = {
    placeId: string;
    ownerUserId: string | null;
    createdAt: string;
    oldKind: string;
    newKind: string | null;
    oldAddressText: string | null;
    newAddressText: string | null;
    oldDescription: string | null;
    newDescription: string | null;
    oldServicesDescription: string | null;
    newServicesDescription: string | null;
    oldExtraInfo: string | null;
    newExtraInfo: string | null;
};

type ChangesResponse = {
    isSuccess: boolean;
    error: string | null;
    totalPending: number;
    items: PendingChangeItem[];
};

type RejectReason = { id: number; code: string; name: string };
type RejectReasonsResponse = { isSuccess: boolean; error: string | null; items: RejectReason[] };

type PlaceKind = { kind: string; name: string };

/** Noraidīšanas dialoga mērķis - vai noraidām jaunu vietu, vai izmaiņu. */
type RejectTarget = { placeId: string; isChange: boolean };


const PAGE_SIZE = 20;


/**
 * Viena lauka diff rinda. Nemainītos laukus neizvada vispār - moderatoram jāredz tikai
 * tas, kas tiešām mainījies.
 */
function DiffRow({ label, oldValue, newValue }: {
    label: string;
    oldValue: string | null;
    newValue: string | null;
}) {
    if ((oldValue ?? '') === (newValue ?? '')) return null;
    return (
        <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography
                variant="body2"
                sx={{ textDecoration: 'line-through', color: 'error.main', whiteSpace: 'pre-wrap' }}
            >
                {oldValue || '(tukšs)'}
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: 'success.main', whiteSpace: 'pre-wrap' }}
            >
                {newValue || '(tukšs)'}
            </Typography>
        </Box>
    );
}


export function PlacesModerationPage() {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [changesPage, setChangesPage] = useState(1);
    const [busyPlaceId, setBusyPlaceId] = useState<string | null>(null);

    // Noraidīšanas dialogs - kopīgs abiem tipiem; isChange nosaka, uz kuru endpointu sūtīt.
    const [rejectTarget, setRejectTarget] = useState<RejectTarget | null>(null);
    const [rejectReasonId, setRejectReasonId] = useState<number | ''>('');
    const [rejectMessage, setRejectMessage] = useState('');

    const skip = (page - 1) * PAGE_SIZE;
    const changesSkip = (changesPage - 1) * PAGE_SIZE;

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ['admin-place-moderation', skip],
        queryFn: async () => {
            const resp = await api.post<PendingResponse>(
                '/api/v1/admin/moderation/places/pending',
                { skip, take: PAGE_SIZE },
            );
            return resp.data;
        },
        staleTime: 30_000,
    });

    const { data: changesData, isLoading: changesLoading, error: changesError } = useQuery({
        queryKey: ['admin-place-change-moderation', changesSkip],
        queryFn: async () => {
            const resp = await api.post<ChangesResponse>(
                '/api/v1/admin/moderation/places/changes/pending',
                { skip: changesSkip, take: PAGE_SIZE },
            );
            return resp.data;
        },
        staleTime: 30_000,
    });

    // Noraidīšanas iemesli (reference tabula) - dropdown izvēlei. Reti mainās → ilgs cache.
    const { data: reasonsData } = useQuery({
        queryKey: ['admin-place-reject-reasons'],
        queryFn: async () => {
            const resp = await api.get<RejectReasonsResponse>(
                '/api/v1/admin/moderation/places/reject-reasons',
            );
            return resp.data;
        },
        staleTime: Infinity,
    });
    const rejectReasons = reasonsData?.items ?? [];

    // Vietu tipu nosaukumi - izmaiņu diff Kind ir Guid, šeit kartējam uz nosaukumu.
    const { data: kinds = [] } = useQuery({
        queryKey: ['admin-place-kinds'],
        queryFn: async () => {
            const resp = await api.get<PlaceKind[]>('/api/v1/admin/place-kinds');
            return resp.data;
        },
        staleTime: Infinity,
    });
    const kindName = (id: string | null) => (id ? kinds.find((k) => k.kind === id)?.name ?? '—' : '—');

    const decideMutation = useMutation({
        mutationFn: async (args: {
            placeId: string;
            accepted: boolean;
            isChange: boolean;
            rejectReasonId?: number;
            rejectMessage?: string;
        }) => {
            const url = args.isChange
                ? '/api/v1/admin/moderation/places/changes/decide'
                : '/api/v1/admin/moderation/places/decide';
            const resp = await api.post<{ isSuccess: boolean; error: string | null }>(url, {
                placeId: args.placeId,
                accepted: args.accepted,
                rejectReasonId: args.rejectReasonId,
                rejectMessage: args.rejectMessage,
            });
            if (!resp.data.isSuccess) {
                throw new Error(resp.data.error || 'Lēmums neizdevās.');
            }
            return args;
        },
        onMutate: (args) => setBusyPlaceId(args.placeId),
        onSettled: () => setBusyPlaceId(null),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-place-moderation'] });
            queryClient.invalidateQueries({ queryKey: ['admin-place-change-moderation'] });
        },
    });

    function openRejectDialog(placeId: string, isChange: boolean) {
        setRejectTarget({ placeId, isChange });
        setRejectReasonId('');
        setRejectMessage('');
    }

    function submitReject() {
        if (rejectTarget == null || rejectReasonId === '') return;
        decideMutation.mutate({
            placeId: rejectTarget.placeId,
            accepted: false,
            isChange: rejectTarget.isChange,
            rejectReasonId: rejectReasonId,
            rejectMessage: rejectMessage.trim() || undefined,
        });
        setRejectTarget(null);
    }

    const items = data?.items ?? [];
    const totalPending = data?.totalPending ?? 0;
    const pageCount = Math.max(1, Math.ceil(totalPending / PAGE_SIZE));

    const changeItems = changesData?.items ?? [];
    const totalChanges = changesData?.totalPending ?? 0;
    const changesPageCount = Math.max(1, Math.ceil(totalChanges / PAGE_SIZE));

    useEffect(() => {
        if (!isLoading && items.length === 0 && page > 1) {
            setPage((p) => Math.max(1, p - 1));
        }
    }, [items.length, isLoading, page]);

    useEffect(() => {
        if (!changesLoading && changeItems.length === 0 && changesPage > 1) {
            setChangesPage((p) => Math.max(1, p - 1));
        }
    }, [changeItems.length, changesLoading, changesPage]);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                    <Typography variant="h4">Vietu moderācija</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Apstiprinātās jaunās vietas kļūst publiski redzamas kartē; noraidītās paliek
                        slēptas. Izmaiņas jau apstiprinātām vietām tiek skatītas atsevišķi - vieta pa
                        to laiku paliek publiska ar veco saturu. Pēc lēmuma īpašniekam tiek nosūtīts
                        push paziņojums.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Chip
                        label={`Jaunas: ${totalPending}`}
                        color={totalPending > 0 ? 'warning' : 'default'}
                    />
                    <Chip
                        label={`Izmaiņas: ${totalChanges}`}
                        color={totalChanges > 0 ? 'info' : 'default'}
                    />
                    <Tooltip title="Atjaunot">
                        <span>
                            <IconButton onClick={() => refetch()} disabled={isFetching}>
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {/* ------------------------------- Jaunas vietas ------------------------------- */}
            <Typography variant="h6" sx={{ mb: 1 }}>Jaunas vietas</Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Neizdevās ielādēt moderācijas rindu: {String((error as Error).message)}
                </Alert>
            )}

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!isLoading && items.length === 0 && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    Nav jaunu vietu, ko moderēt.
                </Alert>
            )}

            <Stack spacing={1.5}>
                {items.map((it) => (
                    <Card key={it.placeId}>
                        <CardContent>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                        <Typography variant="subtitle1" noWrap title={it.placeAddress ?? ''}>
                                            {it.placeAddress ?? '(adrese nezināma)'}
                                        </Typography>
                                        {it.isPaid && (
                                            <Chip
                                                icon={<PaidOutlinedIcon />}
                                                label="Apmaksāts"
                                                size="small"
                                                color="success"
                                            />
                                        )}
                                        {it.placeKindName && (
                                            <Chip label={it.placeKindName} size="small" variant="outlined" />
                                        )}
                                    </Stack>
                                    {it.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            {it.description}
                                        </Typography>
                                    )}
                                    <Stack direction="row" spacing={2}>
                                        <Typography variant="caption" color="text.secondary">
                                            Pievienots {new Date(it.createdAt).toLocaleString('lv-LV')}
                                        </Typography>
                                        {it.ownerUserId && (
                                            <Tooltip title={it.ownerUserId}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontFamily: 'monospace' }}
                                                    noWrap
                                                >
                                                    Īpašnieks: {it.ownerUserId.slice(0, 8)}…
                                                </Typography>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        color="success"
                                        variant="contained"
                                        startIcon={<CheckCircleOutlineIcon />}
                                        disabled={busyPlaceId === it.placeId}
                                        onClick={() => decideMutation.mutate({
                                            placeId: it.placeId,
                                            accepted: true,
                                            isChange: false,
                                        })}
                                    >
                                        Apstiprināt
                                    </Button>
                                    <Button
                                        color="error"
                                        variant="outlined"
                                        startIcon={<HighlightOffIcon />}
                                        disabled={busyPlaceId === it.placeId}
                                        onClick={() => openRejectDialog(it.placeId, false)}
                                    >
                                        Noraidīt
                                    </Button>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                ))}
            </Stack>

            {pageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        page={page}
                        count={pageCount}
                        onChange={(_, p) => setPage(p)}
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            <Divider sx={{ my: 4 }} />

            {/* --------------------------------- Izmaiņas --------------------------------- */}
            <Typography variant="h6" sx={{ mb: 0.5 }}>Izmaiņas apstiprinātām vietām</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Vieta joprojām ir publiska ar veco saturu. Rāda tikai mainītos laukus:
                <span style={{ textDecoration: 'line-through' }}> vecais</span> → jaunais.
            </Typography>

            {changesError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Neizdevās ielādēt izmaiņu rindu: {String((changesError as Error).message)}
                </Alert>
            )}

            {changesLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!changesLoading && changeItems.length === 0 && (
                <Alert severity="success">Nav izmaiņu, ko moderēt.</Alert>
            )}

            <Stack spacing={1.5}>
                {changeItems.map((it) => (
                    <Card key={it.placeId}>
                        <CardContent>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                        <Typography variant="subtitle1" noWrap title={it.oldAddressText ?? ''}>
                                            {it.oldAddressText ?? '(adrese nezināma)'}
                                        </Typography>
                                        <Chip label="Izmaiņa" size="small" color="info" />
                                    </Stack>

                                    <DiffRow
                                        label="Vietas tips"
                                        oldValue={kindName(it.oldKind)}
                                        newValue={kindName(it.newKind)}
                                    />
                                    <DiffRow
                                        label="Adrese"
                                        oldValue={it.oldAddressText}
                                        newValue={it.newAddressText}
                                    />
                                    <DiffRow
                                        label="Apraksts"
                                        oldValue={it.oldDescription}
                                        newValue={it.newDescription}
                                    />
                                    <DiffRow
                                        label="Pakalpojumu apraksts"
                                        oldValue={it.oldServicesDescription}
                                        newValue={it.newServicesDescription}
                                    />
                                    <DiffRow
                                        label="Papildu info"
                                        oldValue={it.oldExtraInfo}
                                        newValue={it.newExtraInfo}
                                    />

                                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Iesniegts {new Date(it.createdAt).toLocaleString('lv-LV')}
                                        </Typography>
                                        {it.ownerUserId && (
                                            <Tooltip title={it.ownerUserId}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontFamily: 'monospace' }}
                                                    noWrap
                                                >
                                                    Īpašnieks: {it.ownerUserId.slice(0, 8)}…
                                                </Typography>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </Box>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <Button
                                        color="success"
                                        variant="contained"
                                        startIcon={<CheckCircleOutlineIcon />}
                                        disabled={busyPlaceId === it.placeId}
                                        onClick={() => decideMutation.mutate({
                                            placeId: it.placeId,
                                            accepted: true,
                                            isChange: true,
                                        })}
                                    >
                                        Publicēt
                                    </Button>
                                    <Button
                                        color="error"
                                        variant="outlined"
                                        startIcon={<HighlightOffIcon />}
                                        disabled={busyPlaceId === it.placeId}
                                        onClick={() => openRejectDialog(it.placeId, true)}
                                    >
                                        Noraidīt
                                    </Button>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                ))}
            </Stack>

            {changesPageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        page={changesPage}
                        count={changesPageCount}
                        onChange={(_, p) => setChangesPage(p)}
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {/* Noraidīšanas dialogs - obligāts iemesls + neobligāta ziņa; kopīgs abiem tipiem. */}
            <Dialog open={rejectTarget !== null} onClose={() => setRejectTarget(null)} fullWidth maxWidth="sm">
                <DialogTitle>
                    {rejectTarget?.isChange ? 'Noraidīt izmaiņu' : 'Noraidīt vietu'}
                </DialogTitle>
                <DialogContent>
                    {rejectTarget?.isChange && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Vieta paliks publiska ar veco saturu. SP redzēs iemeslu un varēs labot.
                        </Alert>
                    )}
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth required>
                            <InputLabel id="reject-reason-label">Iemesls</InputLabel>
                            <Select
                                labelId="reject-reason-label"
                                label="Iemesls"
                                value={rejectReasonId}
                                onChange={(e) => setRejectReasonId(e.target.value as number)}
                            >
                                {rejectReasons.map((r) => (
                                    <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Paskaidrojums (neobligāts)"
                            value={rejectMessage}
                            onChange={(e) => setRejectMessage(e.target.value)}
                            multiline
                            minRows={2}
                            fullWidth
                            placeholder="Piem., attēls satur nepiemērotu saturu."
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectTarget(null)}>Atcelt</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={rejectReasonId === ''}
                        onClick={submitReject}
                    >
                        Noraidīt
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
